import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { previewVideo } from '@/services/youtube.service'
import { useAddYouTubeVideo } from '@/hooks/useAddYouTubeVideo'
import type { YouTubePreviewResponse } from '@/types/youtube.types'

interface YouTubeUrlInputProps {
  projectId: number
}

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'preview'; title: string; thumbnailUrl: string; youtubeVideoId: string }
  | { kind: 'embedding-disabled'; title: string; thumbnailUrl: string }
  | { kind: 'invalid-url'; message: string }
  | { kind: 'api-error'; message: string }

const YOUTUBE_URL_PATTERN =
  /^https?:\/\/((www\.|m\.)?youtube\.com\/(watch\?.*v=|embed\/|shorts\/)|youtu\.be\/).+/i

function isValidYouTubeUrl(url: string): boolean {
  return YOUTUBE_URL_PATTERN.test(url)
}

function derivePreviewState(data: YouTubePreviewResponse): PreviewState {
  if (data.status === 'failed') {
    return { kind: 'api-error', message: data.message ?? 'Something went wrong loading the preview. Please try again.' }
  }
  if (data.embedding_allowed === false) {
    return {
      kind: 'embedding-disabled',
      title: data.title ?? '',
      thumbnailUrl: data.thumbnail_url ?? '',
    }
  }
  return {
    kind: 'preview',
    title: data.title ?? '',
    thumbnailUrl: data.thumbnail_url ?? '',
    youtubeVideoId: data.youtube_video_id ?? '',
  }
}

const DEBOUNCE_MS = 500

export const YouTubeUrlInput = ({ projectId }: YouTubeUrlInputProps) => {
  const [url, setUrl] = useState('')
  const [previewState, setPreviewState] = useState<PreviewState>({ kind: 'idle' })
  // Tracks the URL sent with the most-recently fired mutation so stale
  // out-of-order responses from earlier requests are discarded.
  const pendingUrlRef = useRef<string>('')

  const addVideoMutation = useAddYouTubeVideo()
  const { reset: resetAddMutation } = addVideoMutation

  const { mutate: triggerPreview, isPending } = useMutation({
    mutationFn: previewVideo,
    onSuccess: (data, variables) => {
      if (variables.url !== pendingUrlRef.current) return
      setPreviewState(derivePreviewState(data))
    },
    onError: (_err, variables) => {
      if (variables.url !== pendingUrlRef.current) return
      setPreviewState({
        kind: 'api-error',
        message: 'Something went wrong loading the preview. Please try again.',
      })
    },
  })

  // Validate immediately on every keystroke; debounce the API call by DEBOUNCE_MS.
  // Also reset any stale add-mutation state so duplicate alerts don't bleed into the next preview.
  useEffect(() => {
    resetAddMutation()

    if (!url) {
      // Clear pending ref so any in-flight response from the previous URL
      // cannot match and repopulate the preview area after the field is cleared.
      pendingUrlRef.current = ''
      setPreviewState({ kind: 'idle' })
      return
    }

    if (!isValidYouTubeUrl(url)) {
      // Discard any in-flight response for a URL that is no longer considered valid.
      pendingUrlRef.current = ''
      setPreviewState({ kind: 'invalid-url', message: 'Enter a valid YouTube URL' })
      return
    }

    setPreviewState({ kind: 'loading' })
    const timer = setTimeout(() => {
      pendingUrlRef.current = url
      triggerPreview({ url })
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [url, triggerPreview, resetAddMutation])

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value)
  }

  const handleAddToProject = async () => {
    if (previewState.kind !== 'preview') return
    const result = await addVideoMutation.mutateAsync({
      project_id: projectId,
      youtube_video_id: previewState.youtubeVideoId,
    })
    if (result.status === 'success') {
      setPreviewState({ kind: 'idle' })
      setUrl('')
    }
    // If result.status === 'failed' (duplicate), the component shows the inline Alert below
  }

  const urlError = previewState.kind === 'invalid-url' ? previewState.message : undefined

  return (
    <Box>
      <TextField
        fullWidth
        label="YouTube URL"
        placeholder="https://www.youtube.com/watch?v=..."
        value={url}
        onChange={handleUrlChange}
        error={previewState.kind === 'invalid-url'}
        helperText={urlError}
        variant="outlined"
      />

      {previewState.kind === 'loading' && (
        <Box mt={2}>
          <Skeleton variant="rectangular" height={180} />
          <Skeleton variant="text" sx={{ mt: 1 }} />
        </Box>
      )}

      {previewState.kind === 'preview' && (
        <Box mt={2} display="flex" flexDirection="column" gap={1}>
          <Box component="img"
            src={previewState.thumbnailUrl}
            alt={previewState.title}
            sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1 }}
          />
          <Typography variant="subtitle1" fontWeight="medium">
            {previewState.title}
          </Typography>
          {addVideoMutation.data?.status === 'failed' && addVideoMutation.data.message && (
            <Alert severity="info">{addVideoMutation.data.message}</Alert>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddToProject}
            disabled={addVideoMutation.isPending}
          >
            {addVideoMutation.isPending ? 'Adding...' : 'Add to Project'}
          </Button>
        </Box>
      )}

      {previewState.kind === 'embedding-disabled' && (
        <Box mt={2}>
          {previewState.thumbnailUrl && (
            <Box component="img"
              src={previewState.thumbnailUrl}
              alt={previewState.title}
              sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1, mb: 1 }}
            />
          )}
          {previewState.title && (
            <Typography variant="subtitle1" fontWeight="medium" mb={1}>
              {previewState.title}
            </Typography>
          )}
          <Alert severity="warning">
            This video cannot be played within Scribelate. Please choose a different video.
          </Alert>
        </Box>
      )}

      {previewState.kind === 'api-error' && (
        <Box mt={2}>
          <Alert severity="error">{previewState.message}</Alert>
        </Box>
      )}

      {isPending && previewState.kind !== 'loading' && (
        <Box display="flex" justifyContent="center" mt={1}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  )
}
